import {
  Clock,
  CheckCircle2,
  ShieldX,
  XCircle,
  Lock,
  CircleDashed,
  type LucideProps,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/cn'
import type { BlockStatus } from '../types'

const VARIANT: Record<BlockStatus, React.ComponentProps<typeof Badge>['variant']> = {
  requested: 'pending',
  pending: 'pending',
  approved: 'info',
  active: 'failed',
  lifted: 'cancelled',
  rejected: 'failed',
  expired: 'cancelled',
}

const LABEL: Record<BlockStatus, string> = {
  requested: 'Awaiting approval',
  pending: 'Pending',
  approved: 'Approved',
  active: 'Active',
  lifted: 'Lifted',
  rejected: 'Rejected',
  expired: 'Expired',
}

// Icons give the badge a non-colour signal so deuteranopic operators
// can tell active (red lock) from lifted (grey dashed circle) even
// when the muted variants drift toward the same hue.
const ICON: Record<BlockStatus, ComponentType<LucideProps>> = {
  requested: Clock,
  pending: Clock,
  approved: CheckCircle2,
  active: Lock,
  lifted: CircleDashed,
  rejected: ShieldX,
  expired: XCircle,
}

/**
 * AF-128 — block status chip with the AC-mandated palette:
 *   pending → amber, active → red, lifted → grey,
 *   rejected → red strikethrough, expired → grey.
 */
export function BlockStatusBadge({ status }: { status: BlockStatus }) {
  const Icon = ICON[status]
  return (
    <Badge
      variant={VARIANT[status]}
      className={cn('capitalize', status === 'rejected' && 'line-through')}
    >
      <Icon size={12} aria-hidden />
      {LABEL[status]}
    </Badge>
  )
}
